import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Modal, ModalBody, ModalHeader} from 'reactstrap';
import {CaretDown, CheckCircle, FunnelSimple, MagnifyingGlass, Minus, Plus, WarningCircle} from '@phosphor-icons/react';
import {APIClient} from '../../../utils/APIClient';
import {API_URL} from '../../../settings/local_settings';
import {
    CHANNEL_INFO_BY_CHANNEL,
    CHANNEL_TYPE_ECOMMERCE,
    CHANNEL_TYPE_EMAIL_MARKETING,
    CHANNEL_TYPE_PAID_MEDIA,
    FACEBOOK_CHANNEL,
    GOOGLE_CHANNEL,
    LINKEDIN_CHANNEL,
    TIKTOK_CHANNEL,
} from '../../channels/channels';
import {ChannelLogo} from '../../channels/ChannelLogo';
import {GoogleLinkComponent} from '../../agency/link_acocunts/GoogleLinkt';
import {FacebookLinkComponent} from '../../agency/link_acocunts/FacebookLink';
import {TikTokLinkComponent} from '../../agency/link_acocunts/TiktokLink';
import {LinkedinLinkComponent} from '../../agency/link_acocunts/LinkedinLink';
import {Spinner} from '../../Spinner';
import {StepLayout} from '../StepLayout';

const DEFAULT_CHANNELS = [GOOGLE_CHANNEL, FACEBOOK_CHANNEL, LINKEDIN_CHANNEL, TIKTOK_CHANNEL];
const DIRECT_OAUTH_CHANNELS = [GOOGLE_CHANNEL, FACEBOOK_CHANNEL, LINKEDIN_CHANNEL, TIKTOK_CHANNEL];

const MOCK_ACCOUNTS_BY_CHANNEL = {
    [GOOGLE_CHANNEL]: [
        {id: 'mock-google-1', name: 'Dashbo Demo Google Ads'},
        {id: 'mock-google-2', name: 'Performance Latam'},
    ],
    [FACEBOOK_CHANNEL]: [
        {id: 'mock-facebook-1', name: 'Dashbo Demo Meta Ads'},
        {id: 'mock-facebook-2', name: 'Social Ads Studio'},
    ],
    [LINKEDIN_CHANNEL]: [
        {id: 'mock-linkedin-1', name: 'LinkedIn B2B Growth'},
    ],
    [TIKTOK_CHANNEL]: [
        {id: 'mock-tiktok-1', name: 'TikTok Always On'},
    ],
};

function getChannelName(channel) {
    return CHANNEL_INFO_BY_CHANNEL[channel]?.Spanish || channel;
}

function normalizeAccountId(account) {
    return String(account?.account_id || account?.ad_account_id || account?.store_id || account?.id || '');
}

function normalizeAvailableAccountId(account) {
    return String(account?.id || account?.account_id || account?.store_id || '');
}

function getAccountName(account) {
    return account?.name || account?.name_with_parents || account?.account_name || account?.store_name || normalizeAccountId(account);
}

function getLinkedAccountsForChannel(channel, adAccounts, stores, mockLinkedByChannel) {
    const linked = [...(adAccounts || []), ...(stores || [])].filter((account) => account.channel === channel);
    return [...linked, ...(mockLinkedByChannel[channel] || [])];
}

function getLinkedAccountId(account) {
    return account?.id || account?.agency_client_ad_account_id || account?.agency_client_store_id || null;
}

function getSelectableAccountKey(channel, account) {
    return `${channel}:${normalizeAvailableAccountId(account)}`;
}

function getLinkedSelectableAccountKey(channel, account) {
    return `linked:${channel}:${getLinkedAccountId(account) || normalizeAccountId(account)}`;
}

function compactStringValues(values) {
    return values
        .filter((value) => value !== null && value !== undefined && value !== '')
        .map((value) => value.toString());
}

function getCanonicalAccountClientIds(account) {
    return compactStringValues([
        account?.id,
        account?.agency_client_ad_account_id,
        account?.agency_client_store_id,
        account?.client_ad_account_id,
        account?.client_store_id,
    ]);
}

function getCanonicalAccountExternalIds(account) {
    return compactStringValues([
        account?.account_id,
        account?.ad_account_account_id,
        account?.store_id,
        account?.ad_account_id,
    ]);
}

function findLinkedAccountForSelection(item, linkedAccounts) {
    return (linkedAccounts || []).find((account) => (
        account.channel === item.channel && normalizeAccountId(account) === item.accountId.toString()
    ));
}

function getSelectionIdentity(item, linkedAccounts, linkedClientIdsByKey = {}) {
    const linkedAccount = item.isAlreadyLinked ? item.account : findLinkedAccountForSelection(item, linkedAccounts);
    const linkedClientAccountId = linkedClientIdsByKey[item.key] || getLinkedAccountId(linkedAccount);

    return {
        channel: item.channel,
        name: item.name,
        accountId: item.accountId ? item.accountId.toString() : null,
        clientAccountId: linkedClientAccountId ? linkedClientAccountId.toString() : (item.clientAccountId ? item.clientAccountId.toString() : null),
    };
}

function findCanonicalAccountForSelection(identity, canonicalAccounts) {
    return (canonicalAccounts || []).find((account) => {
        if (account.channel !== identity.channel) return false;

        const clientIds = getCanonicalAccountClientIds(account);
        if (identity.clientAccountId && clientIds.includes(identity.clientAccountId)) return true;

        const externalIds = getCanonicalAccountExternalIds(account);
        if (identity.accountId && externalIds.includes(identity.accountId)) return true;

        return false;
    });
}

async function fetchBusinessUnitAvailableAccounts(businessUnitId, t) {
    const response = await new APIClient(`${API_URL}business-unit/${businessUnitId}/all_ad_accounts`).call('GET');
    if (!response || response.status !== 'OK') {
        throw new Error(response?.error_message || t('onboarding_wizard.step3_link_accounts_error'));
    }

    return response.data || [];
}

async function fetchLinkedClientAccounts(clientId, t) {
    const clientResponse = await new APIClient(`${API_URL}client/${clientId}`).call('GET');
    if (!clientResponse || clientResponse.status !== 'OK') {
        throw new Error(clientResponse?.error_message || t('onboarding_wizard.step3_link_accounts_error'));
    }

    return [
        ...(clientResponse.data.client_ad_accounts || []),
        ...(clientResponse.data.client_stores || []),
    ];
}

async function linkSelectedAccountsToClientDraft(clientId, selectedItems, t) {
    if (selectedItems.length === 0) return {linkedAccounts: [], linkedClientIdsByKey: {}};

    const newItems = selectedItems.filter((item) => !item.isAlreadyLinked);
    const realItems = newItems.filter((item) => !item.isMock);
    const linkedClientIdsByKey = {};

    if (realItems.length > 0) {
        const responses = await Promise.all(realItems.map(({channel, accountId}) =>
            new APIClient(`${API_URL}client/${clientId}/ad_account/add`).call('POST', {
                client_id: clientId,
                channel,
                account_id: accountId.toString(),
                daily_limit_type: null,
                daily_limit_currency: null,
                daily_limit_value: null,
            })
        ));

        const failedResponse = responses.find((data) => !data || data.status !== 'OK');
        if (failedResponse !== undefined) {
            throw new Error(failedResponse?.error_message || t('common.error'));
        }

        responses.forEach((response, index) => {
            const linkedClientId = response?.data?.agency_client_ad_account_id || response?.data?.agency_client_store_id;
            if (linkedClientId) {
                linkedClientIdsByKey[realItems[index].key] = linkedClientId.toString();
            }
        });
    }

    const linkedAccounts = await fetchLinkedClientAccounts(clientId, t);
    return {linkedAccounts, linkedClientIdsByKey};
}

async function resolveSelectedAccountIdsForBusinessUnitDraft(businessUnitId, selectedItems, linkedAccounts, linkedClientIdsByKey, t) {
    if (!businessUnitId) return [];

    const canonicalAccounts = await fetchBusinessUnitAvailableAccounts(businessUnitId, t);
    const identities = selectedItems
        .filter((item) => !item.isMock)
        .map((item) => getSelectionIdentity(item, linkedAccounts, linkedClientIdsByKey));

    const resolvedIds = [];
    const unresolved = [];

    identities.forEach((identity) => {
        const canonicalAccount = findCanonicalAccountForSelection(identity, canonicalAccounts);
        if (canonicalAccount) {
            resolvedIds.push(canonicalAccount.id);
        } else {
            unresolved.push(identity.name);
        }
    });

    if (unresolved.length > 0) {
        throw new Error(t('onboarding_wizard.step3_resolve_accounts_error', {accounts: unresolved.join(', ')}));
    }

    return Array.from(new Set(resolvedIds.map((id) => id.toString())));
}

async function associateClientAccountsToBusinessUnitDraft(businessUnitId, accountIds, t) {
    if (!businessUnitId || accountIds.length === 0) return;

    const response = await new APIClient(`${API_URL}business-unit/${businessUnitId}/campaigns-configurations/add`).call('POST', accountIds);
    if (!response || response.status !== 'OK') {
        throw new Error(response?.error_message || t('onboarding_wizard.step3_link_accounts_error'));
    }
}

export async function persistOnboardingPlatformDraft({clientId, businessUnitId, selectedItems, t}) {
    const {linkedAccounts, linkedClientIdsByKey} = await linkSelectedAccountsToClientDraft(clientId, selectedItems || [], t);
    const accountIdsToAssociate = await resolveSelectedAccountIdsForBusinessUnitDraft(
        businessUnitId,
        selectedItems || [],
        linkedAccounts,
        linkedClientIdsByKey,
        t
    );
    await associateClientAccountsToBusinessUnitDraft(businessUnitId, accountIdsToAssociate, t);
}

export function Step2Platforms({clientId, businessUnitId, draftMode, initialSelectedAccountKeys, onComplete, onDraftComplete, onBack, stepNumber = 2}) {
    const {t} = useTranslation();
    const [loading, setLoading] = useState(true);
    const [channels, setChannels] = useState(DEFAULT_CHANNELS);
    const [accountsByChannel, setAccountsByChannel] = useState({});
    const [adAccounts, setAdAccounts] = useState([]);
    const [stores, setStores] = useState([]);
    const [selectedAccountKeys, setSelectedAccountKeys] = useState(initialSelectedAccountKeys || []);
    const [linkingSelectedAccounts, setLinkingSelectedAccounts] = useState(false);
    const [mockChannels, setMockChannels] = useState({});
    const [mockLinkedByChannel, setMockLinkedByChannel] = useState({});
    const [connectMenuOpen, setConnectMenuOpen] = useState(false);
    const [linkListOpen, setLinkListOpen] = useState(false);
    const [accountSearch, setAccountSearch] = useState('');
    const [accountTypeFilter, setAccountTypeFilter] = useState('all');
    const [accountFilterOpen, setAccountFilterOpen] = useState(false);
    const [connectChannel, setConnectChannel] = useState(null);
    const [connectionName, setConnectionName] = useState('');
    const [associationError, setAssociationError] = useState(null);

    const visibleChannels = useMemo(
        () => channels.filter((channel) => CHANNEL_INFO_BY_CHANNEL[channel]),
        [channels]
    );

    const linkedAccountItems = useMemo(() => (
        visibleChannels.flatMap((channel) =>
            getLinkedAccountsForChannel(channel, adAccounts, stores, mockLinkedByChannel).map((account) => ({
                channel,
                account,
                key: getLinkedSelectableAccountKey(channel, account),
                accountId: normalizeAccountId(account),
                clientAccountId: getLinkedAccountId(account),
                name: getAccountName(account),
                isAlreadyLinked: true,
                isMock: false,
            }))
        )
    ), [visibleChannels, adAccounts, stores, mockLinkedByChannel]);

    const selectableAccountItems = useMemo(() => (
        linkedAccountItems.concat(visibleChannels.flatMap((channel) => {
            const linkedIds = new Set(getLinkedAccountsForChannel(channel, adAccounts, stores, mockLinkedByChannel).map(normalizeAccountId));
            return (accountsByChannel[channel] || [])
                .filter((account) => !linkedIds.has(normalizeAvailableAccountId(account)))
                .map((account) => ({
                    channel,
                    account,
                    key: getSelectableAccountKey(channel, account),
                    accountId: normalizeAvailableAccountId(account),
                    name: getAccountName(account),
                    isAlreadyLinked: false,
                    isMock: !!mockChannels[channel],
                }));
        }))
    ), [visibleChannels, accountsByChannel, adAccounts, stores, mockLinkedByChannel, linkedAccountItems, mockChannels]);

    const accountTypeOptions = useMemo(() => {
        const channelsWithAccounts = Array.from(new Set(selectableAccountItems.map((item) => item.channel)));
        return channelsWithAccounts.map((channel) => ({
            channel,
            label: getChannelName(channel),
        }));
    }, [selectableAccountItems]);

    const filteredSelectableAccountItems = useMemo(() => {
        const normalizedSearch = accountSearch.trim().toLowerCase();

        return selectableAccountItems.filter((item) => {
            const matchesType = accountTypeFilter === 'all' || item.channel === accountTypeFilter;
            const matchesSearch = !normalizedSearch || item.name.toLowerCase().includes(normalizedSearch);
            return matchesType && matchesSearch;
        });
    }, [selectableAccountItems, accountSearch, accountTypeFilter]);

    const fetchAvailableAccounts = useCallback(async (nextChannels) => {
        const entries = await Promise.all(nextChannels.map(async (channel) => {
            try {
                const response = await new APIClient(`${API_URL}get-accounts/${channel}`).call('GET');
                if (response && response.status === 'OK') {
                    return [channel, response.data || [], false];
                }
                return [channel, MOCK_ACCOUNTS_BY_CHANNEL[channel] || [], true];
            } catch (e) {
                return [channel, MOCK_ACCOUNTS_BY_CHANNEL[channel] || [], true];
            }
        }));

        const nextAccounts = {};
        const nextMockChannels = {};
        entries.forEach(([channel, accounts, isMock]) => {
            nextAccounts[channel] = accounts;
            if (isMock) nextMockChannels[channel] = true;
        });
        setAccountsByChannel(nextAccounts);
        setMockChannels(nextMockChannels);
    }, []);

    const fetchClientData = useCallback(async () => {
        setLoading(true);
        let nextChannels = DEFAULT_CHANNELS;

        try {
            const [clientRes, paidChannelsRes, ecommerceChannelsRes, emailChannelsRes] = await Promise.all([
                clientId ? new APIClient(`${API_URL}client/${clientId}`).call('GET').catch(() => null) : Promise.resolve(null),
                new APIClient(`${API_URL}get-channels/${CHANNEL_TYPE_PAID_MEDIA}/`).call('GET').catch(() => null),
                new APIClient(`${API_URL}get-channels/${CHANNEL_TYPE_ECOMMERCE}/`).call('GET').catch(() => null),
                new APIClient(`${API_URL}get-channels/${CHANNEL_TYPE_EMAIL_MARKETING}/`).call('GET').catch(() => null),
            ]);

            if (clientRes && clientRes.status === 'OK') {
                setAdAccounts(clientRes.data.client_ad_accounts || []);
                setStores(clientRes.data.client_stores || []);
            }

            const enabledPaidChannels = paidChannelsRes && paidChannelsRes.status === 'OK' ? paidChannelsRes.data : [];
            const enabledStoreChannels = ecommerceChannelsRes && ecommerceChannelsRes.status === 'OK' ? ecommerceChannelsRes.data : [];
            const enabledEmailChannels = emailChannelsRes && emailChannelsRes.status === 'OK' ? emailChannelsRes.data : [];
            const enabledChannels = [...enabledPaidChannels, ...enabledStoreChannels, ...enabledEmailChannels];
            const clientChannels = clientRes && clientRes.status === 'OK' ? (clientRes.data.channels || []) : [];
            const mergedChannels = Array.from(new Set([...enabledChannels, ...clientChannels, ...DEFAULT_CHANNELS]));

            nextChannels = mergedChannels.filter((channel) => CHANNEL_INFO_BY_CHANNEL[channel]);
            setChannels(nextChannels);
            await fetchAvailableAccounts(nextChannels);
        } finally {
            setLoading(false);
        }
    }, [clientId, fetchAvailableAccounts]);

    useEffect(() => {
        fetchClientData();
    }, [fetchClientData]);

    const fetchLinkedClientAccounts = async () => {
        const clientResponse = await new APIClient(`${API_URL}client/${clientId}`).call('GET');
        if (!clientResponse || clientResponse.status !== 'OK') {
            throw new Error(clientResponse?.error_message || t('onboarding_wizard.step3_link_accounts_error'));
        }

        const nextAdAccounts = clientResponse.data.client_ad_accounts || [];
        const nextStores = clientResponse.data.client_stores || [];
        setAdAccounts(nextAdAccounts);
        setStores(nextStores);

        return [...nextAdAccounts, ...nextStores];
    };

    const handleOpenConnectModal = (channel) => {
        setConnectMenuOpen(false);
        setConnectChannel(channel);
        setConnectionName('');
    };

    const handleAccountTypeFilter = (channel) => {
        setAccountTypeFilter(channel);
        setAccountFilterOpen(false);
    };

    const handleCloseConnectModal = () => {
        setConnectChannel(null);
        setConnectionName('');
    };

    const handleBeforeConnect = () => {
        if (connectionName.trim()) {
            localStorage.setItem('pending_credential_label', connectionName.trim());
        }
    };

    const handleToggleAccountSelection = (accountKey) => {
        setSelectedAccountKeys((prev) => (
            prev.includes(accountKey)
                ? prev.filter((key) => key !== accountKey)
                : [...prev, accountKey]
        ));
    };

    const fetchBusinessUnitAvailableAccounts = async () => {
        const response = await new APIClient(`${API_URL}business-unit/${businessUnitId}/all_ad_accounts`).call('GET');
        if (!response || response.status !== 'OK') {
            throw new Error(response?.error_message || t('onboarding_wizard.step3_link_accounts_error'));
        }

        return response.data || [];
    };

    const resolveSelectedAccountIdsForBusinessUnit = async (selectedItems, linkedAccounts, linkedClientIdsByKey = {}) => {
        if (!businessUnitId) return [];

        const canonicalAccounts = await fetchBusinessUnitAvailableAccounts();
        const identities = selectedItems
            .filter((item) => !item.isMock)
            .map((item) => getSelectionIdentity(item, linkedAccounts, linkedClientIdsByKey));

        const resolvedIds = [];
        const unresolved = [];

        identities.forEach((identity) => {
            const canonicalAccount = findCanonicalAccountForSelection(identity, canonicalAccounts);
            if (canonicalAccount) {
                resolvedIds.push(canonicalAccount.id);
            } else {
                unresolved.push(identity.name);
            }
        });

        if (unresolved.length > 0) {
            throw new Error(t('onboarding_wizard.step3_resolve_accounts_error', {accounts: unresolved.join(', ')}));
        }

        return Array.from(new Set(resolvedIds.map((id) => id.toString())));
    };

    const linkSelectedAccountsToClient = async (selectedItems) => {
        if (selectedItems.length === 0) return [];

        const newItems = selectedItems.filter((item) => !item.isAlreadyLinked);
        const realItems = newItems.filter((item) => !item.isMock);
        const mockItems = newItems.filter((item) => item.isMock);
        let linkedAccounts = [...adAccounts, ...stores];
        const linkedClientIdsByKey = {};

        if (mockItems.length > 0) {
            setMockLinkedByChannel((prev) => {
                const next = {...prev};
                mockItems.forEach(({channel, account, accountId}) => {
                    next[channel] = [
                        ...(next[channel] || []),
                        {...account, id: accountId, account_id: accountId, channel},
                    ];
                });
                return next;
            });
        }

        if (realItems.length > 0) {
            const responses = await Promise.all(realItems.map(({channel, accountId}) =>
                new APIClient(`${API_URL}client/${clientId}/ad_account/add`).call('POST', {
                    client_id: clientId,
                    channel,
                    account_id: accountId.toString(),
                    daily_limit_type: null,
                    daily_limit_currency: null,
                    daily_limit_value: null,
                })
            ));

            const failedResponse = responses.find((data) => !data || data.status !== 'OK');
            if (failedResponse !== undefined) {
                alert(failedResponse?.error_message || t('common.error'));
                return [];
            } else {
                responses.forEach((response, index) => {
                    const linkedClientId = response?.data?.agency_client_ad_account_id || response?.data?.agency_client_store_id;
                    if (linkedClientId) {
                        linkedClientIdsByKey[realItems[index].key] = linkedClientId.toString();
                    }
                });
                linkedAccounts = await fetchLinkedClientAccounts();
            }
        }

        return {linkedAccounts, linkedClientIdsByKey};
    };

    const associateClientAccountsToBusinessUnit = async (accountIds) => {
        if (!businessUnitId) return;
        if (accountIds.length === 0) return;

        const response = await new APIClient(`${API_URL}business-unit/${businessUnitId}/campaigns-configurations/add`).call('POST', accountIds);
        if (!response || response.status !== 'OK') {
            throw new Error(response?.error_message || t('onboarding_wizard.step3_link_accounts_error'));
        }
    };

    const handleNext = async () => {
        setAssociationError(null);

        setLinkingSelectedAccounts(true);
        try {
            const selectedItems = selectableAccountItems.filter((item) => selectedAccountKeys.includes(item.key));
            if (draftMode) {
                await onDraftComplete({
                    selectedItems,
                    selectedAccountKeys,
                });
                return;
            }
            const {linkedAccounts, linkedClientIdsByKey} = await linkSelectedAccountsToClient(selectedItems);
            const accountIdsToAssociate = await resolveSelectedAccountIdsForBusinessUnit(selectedItems, linkedAccounts, linkedClientIdsByKey);
            if (accountIdsToAssociate.length === 0) return;
            await associateClientAccountsToBusinessUnit(accountIdsToAssociate);
            setSelectedAccountKeys([]);
            onComplete();
        } catch (error) {
            setAssociationError(error.message || t('onboarding_wizard.step3_link_accounts_error'));
        } finally {
            setLinkingSelectedAccounts(false);
        }
    };

    const renderOAuthButton = () => {
        const noop = () => {};

        if (connectChannel === GOOGLE_CHANNEL) return <GoogleLinkComponent hasConnection={false} onConnectionDeleted={noop}/>;
        if (connectChannel === FACEBOOK_CHANNEL) return <FacebookLinkComponent hasConnection={false} onConnectionDeleted={noop}/>;
        if (connectChannel === TIKTOK_CHANNEL) return <TikTokLinkComponent hasConnection={false} onConnectionDeleted={noop}/>;
        if (connectChannel === LINKEDIN_CHANNEL) return <LinkedinLinkComponent hasConnection={false} onConnectionDeleted={noop}/>;

        return (
            <button type="button" className="onboarding-btn -primary" onClick={() => window.open('/agency/conections', '_blank')}>
                {t('onboarding_wizard.step2_manage_connections')}
            </button>
        );
    };

    const selectedConnectChannelName = connectChannel ? getChannelName(connectChannel) : '';
    const canStartDirectConnection = DIRECT_OAUTH_CHANNELS.includes(connectChannel) ? connectionName.trim().length > 0 : true;
    const selectedAccountsCount = selectedAccountKeys.length;
    const canContinue = selectedAccountsCount > 0;
    const selectedAccountTypeLabel = accountTypeFilter === 'all' ? t('onboarding_wizard.step2_filter_all_types') : getChannelName(accountTypeFilter);

    return (
        <StepLayout
            stepNumber={stepNumber}
            title={t('onboarding_wizard.step3_title')}
            description={t('onboarding_wizard.step3_description')}
        >
            {loading ? (
                <div className="onboarding-loader">
                    <Spinner color="positive"/>
                </div>
            ) : (
                <>
                    <section className={`onboarding-platform-connect-section${connectMenuOpen ? ' -menu-open' : ''}`}>
                        <div className="onboarding-platform-connect-copy">
                            <h2>{t('onboarding_wizard.step2_connect_title')}</h2>
                            <p>{t('onboarding_wizard.step2_connect_description')}</p>
                        </div>
                        <div className="onboarding-platform-connect-action">
                            <button
                                type="button"
                                className="onboarding-btn -primary onboarding-platform-menu-button"
                                onClick={() => setConnectMenuOpen((open) => !open)}
                                aria-expanded={connectMenuOpen}
                            >
                                <Plus size={16} weight="bold"/>
                                {t('onboarding_wizard.step2_connect_new_account')}
                                <CaretDown size={15} weight="bold"/>
                            </button>
                            {connectMenuOpen && (
                                <div className="onboarding-platform-menu">
                                    {visibleChannels.map((channel) => (
                                        <button
                                            key={channel}
                                            type="button"
                                            className="onboarding-platform-menu-item"
                                            onClick={() => handleOpenConnectModal(channel)}
                                        >
                                            <ChannelLogo channel={channel} width={18} height={18} hspace={0} vspace={0} marginLeft={0} padding={0} useOld={false}/>
                                            <span>{getChannelName(channel)}</span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </section>

                    <section className={`onboarding-platform-link-section${linkListOpen ? ' -open' : ''}`}>
                        <div className="onboarding-platform-section-header">
                            <button
                                type="button"
                                className="onboarding-platform-section-copy-button"
                                onClick={() => setLinkListOpen((open) => !open)}
                                aria-expanded={linkListOpen}
                            >
                                <h2>{t('onboarding_wizard.step2_existing_title')}</h2>
                                <p>{t('onboarding_wizard.step2_link_description')}</p>
                            </button>
                            <button
                                type="button"
                                className="onboarding-platform-toggle-btn"
                                onClick={() => setLinkListOpen((open) => !open)}
                                aria-expanded={linkListOpen}
                                title={t('onboarding_wizard.step2_link_existing_account')}
                                aria-label={t('onboarding_wizard.step2_link_existing_account')}
                            >
                                {linkListOpen ? <Minus size={24} weight="bold"/> : <Plus size={28} weight="bold"/>}
                            </button>
                        </div>
                        {linkListOpen && (
                            <div className="onboarding-platform-link-list">
                                {selectableAccountItems.length === 0 && (
                                    <div className="onboarding-empty-state">
                                        <p>{t('onboarding_wizard.step2_no_available_accounts')}</p>
                                    </div>
                                )}

                                {selectableAccountItems.length > 0 && (
                                    <>
                                        <div className="onboarding-platform-account-tools">
                                            <label className="onboarding-platform-account-search">
                                                <MagnifyingGlass size={17} weight="bold"/>
                                                <input
                                                    type="search"
                                                    value={accountSearch}
                                                    onChange={(event) => setAccountSearch(event.target.value)}
                                                    placeholder={t('onboarding_wizard.step2_search_accounts_placeholder')}
                                                />
                                            </label>

                                            <div className="onboarding-platform-account-filter">
                                                <button
                                                    type="button"
                                                    className="onboarding-platform-filter-button"
                                                    onClick={() => setAccountFilterOpen((open) => !open)}
                                                    aria-expanded={accountFilterOpen}
                                                >
                                                    <FunnelSimple size={16} weight="bold"/>
                                                    <span>{selectedAccountTypeLabel}</span>
                                                    <CaretDown size={14} weight="bold"/>
                                                </button>
                                                {accountFilterOpen && (
                                                    <div className="onboarding-platform-filter-menu">
                                                        <button
                                                            type="button"
                                                            className={accountTypeFilter === 'all' ? '-active' : ''}
                                                            onClick={() => handleAccountTypeFilter('all')}
                                                        >
                                                            {t('onboarding_wizard.step2_filter_all_types')}
                                                        </button>
                                                        {accountTypeOptions.map(({channel, label}) => (
                                                            <button
                                                                key={channel}
                                                                type="button"
                                                                className={accountTypeFilter === channel ? '-active' : ''}
                                                                onClick={() => handleAccountTypeFilter(channel)}
                                                            >
                                                                <ChannelLogo channel={channel} width={16} height={16} hspace={0} vspace={0} marginLeft={0} padding={0} useOld={false}/>
                                                                {label}
                                                            </button>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        <div className="onboarding-platform-account-list">
                                            {filteredSelectableAccountItems.map((item) => {
                                                const isSelected = selectedAccountKeys.includes(item.key);
                                                return (
                                                    <button
                                                        key={item.key}
                                                        type="button"
                                                        className={`onboarding-platform-account-option${isSelected ? ' -selected' : ''}`}
                                                        onClick={() => handleToggleAccountSelection(item.key)}
                                                    >
                                                        <span className="onboarding-platform-account-logo">
                                                            <ChannelLogo channel={item.channel} width={22} height={22} hspace={0} vspace={0} marginLeft={0} padding={0} useOld={false}/>
                                                        </span>
                                                        <span className="onboarding-platform-account-copy">
                                                            <strong>{item.name}</strong>
                                                            <span>{getChannelName(item.channel)} · ID {item.accountId}</span>
                                                        </span>
                                                        {item.isAlreadyLinked && (
                                                            <span className="onboarding-platform-mock-badge -linked">
                                                                <CheckCircle size={13} weight="fill"/>
                                                                {t('onboarding_wizard.step2_already_linked_badge')}
                                                            </span>
                                                        )}
                                                        {item.isMock && (
                                                            <span className="onboarding-platform-mock-badge">
                                                                <WarningCircle size={13} weight="fill"/>
                                                                {t('onboarding_wizard.step2_mock_badge')}
                                                            </span>
                                                        )}
                                                        <span className="onboarding-platform-account-check">
                                                            {isSelected && <CheckCircle size={18} weight="fill"/>}
                                                        </span>
                                                    </button>
                                                );
                                            })}
                                        </div>

                                        {filteredSelectableAccountItems.length === 0 && (
                                            <div className="onboarding-empty-state -compact">
                                                <p>{t('onboarding_wizard.step2_no_filtered_accounts')}</p>
                                            </div>
                                        )}

                                        <div className="onboarding-platform-link-actions">
                                            <span>
                                                {selectedAccountsCount === 0
                                                    ? t('onboarding_wizard.step2_select_accounts_hint')
                                                    : t('onboarding_wizard.step2_selected_accounts_count', {count: selectedAccountsCount})}
                                            </span>
                                        </div>
                                    </>
                                )}

                                {selectableAccountItems.length === 0 && linkedAccountItems.length > 0 && (
                                    <p className="onboarding-platform-row-hint">
                                        {t('onboarding_wizard.step2_all_accounts_linked')}
                                    </p>
                                )}
                            </div>
                        )}
                    </section>
                    {associationError && <p className="onboarding-error">{associationError}</p>}
                    <div className="onboarding-actions -split">
                        <button type="button" className="onboarding-btn -ghost" onClick={onBack}>
                            {t('onboarding_wizard.back_to_previous_step')}
                        </button>
                        <div className="onboarding-actions-group">
                            {!canContinue && (
                                <span className="onboarding-action-hint">{t('onboarding_wizard.step2_required_selection')}</span>
                            )}
                            <button
                                type="button"
                                className="onboarding-btn -primary"
                                onClick={handleNext}
                                disabled={!canContinue || linkingSelectedAccounts}
                            >
                                {linkingSelectedAccounts && <span className="onboarding-btn-spinner" aria-hidden="true"/>}
                                {linkingSelectedAccounts ? t('onboarding_wizard.step2_linking_accounts') : t('onboarding_wizard.next')}
                            </button>
                        </div>
                    </div>
                </>
            )}

            <Modal isOpen={!!connectChannel} toggle={handleCloseConnectModal} className="onboarding-platform-connect-modal">
                <ModalHeader toggle={handleCloseConnectModal}>
                    {connectChannel && <ChannelLogo channel={connectChannel} width={20} height={20} hspace={0} vspace={0}/>}
                    {' '}{t('onboarding_wizard.step2_connect_modal_title', {channel: selectedConnectChannelName})}
                </ModalHeader>
                <ModalBody>
                    <div className="modal-section">
                        {DIRECT_OAUTH_CHANNELS.includes(connectChannel) && (
                            <div className="onboarding-field">
                                <label>{t('add_credential_modal.name_label')}</label>
                                <input
                                    type="text"
                                    value={connectionName}
                                    onChange={(event) => setConnectionName(event.target.value)}
                                    placeholder={t('add_credential_modal.name_placeholder')}
                                />
                                <span className="field-footnote">{t('add_credential_modal.name_hint')}</span>
                            </div>
                        )}

                        <p className="onboarding-platform-connect-modal-copy">
                            {DIRECT_OAUTH_CHANNELS.includes(connectChannel)
                                ? t('onboarding_wizard.step2_connect_modal_description')
                                : t('onboarding_wizard.step2_connect_modal_external_description')}
                        </p>

                        <div
                            className="onboarding-platform-oauth-action"
                            onClickCapture={handleBeforeConnect}
                            style={{pointerEvents: canStartDirectConnection ? 'auto' : 'none', opacity: canStartDirectConnection ? 1 : 0.55}}
                        >
                            {renderOAuthButton()}
                        </div>
                    </div>
                </ModalBody>
            </Modal>
        </StepLayout>
    );
}

export default Step2Platforms;
